import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Play, Image as ImageIcon, Loader2, Download, Plus, ChevronDown, Info, Sparkles, History, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { getStoredToken, useAuth } from '../App';
import { AIClipsLibrary } from './AIClipsLibrary';
import { CoachEye } from './Coach';

// API = full backend URL (env) — kept so the existing string templates in this
// file still work (`${API}/api/...`). getStoredToken reads the correct token
// key used by the whole app (`wim_session_token`), NOT a generic `token` key.
const API = process.env.REACT_APP_BACKEND_URL;

// Engine configurations with real specs and credit costs
const ENGINES = {
  seedance: {
    id: 'seedance',
    name: 'Seedance 1.5',
    tier: 'Budget',
    type: 'video',
    creditsPerSecond: 3,
    minDuration: 4,
    maxDuration: 12,
    durationStep: 1,
    defaultDuration: 5,
    resolutions: ['1280x720', '720x1280', '640x640'],
    supportsRefImage: true,
    maxRefImages: 2, // 1 = start frame, 2 = start + end frame (Seedance supports `last_image`)
    supportsAudio: true,
    description: 'Budget tier with audio + start/end frames. 640x640 = smallest square Seedance produces (ideal for Coach clips).',
    color: 'emerald',
  },
  wan: {
    id: 'wan',
    name: 'Wan 2.6',
    tier: 'Standard',
    type: 'video',
    creditsPerSecond: 5,
    minDuration: 5,
    maxDuration: 15,
    durationStep: 5,
    durationOptions: [5, 10, 15],
    defaultDuration: 5,
    resolutions: ['1280x720', '720x1280'],
    supportsRefImage: true,
    supportsAudio: true,
    description: 'Great quality with audio support',
    color: 'blue',
  },
  grok: {
    id: 'grok',
    name: 'Grok Imagine',
    tier: 'Admin',
    type: 'video',
    creditsPerSecond: 5,
    minDuration: 1,
    maxDuration: 15,
    durationOptions: [3, 5, 8, 12, 15],
    defaultDuration: 5,
    resolutions: ['1280x720', '720x1280', '1024x1024'],
    supportsRefImage: true,
    maxRefImages: 7,
    refImageDurationCap: 10, // xAI caps reference-to-video at 10s
    supportsAudio: false,
    description: 'xAI — text or image → video, native audio (admin beta)',
    color: 'rose',
    adminOnly: true, // Re-enabled May 2026 with v2 payload (base64 data URIs) — bypasses xAI domain whitelist that blocked HP server URLs in v1
    etaHint: 'Beta — using xAI v2 base64 payload. Text-to-video is the most reliable mode; image modes still being verified.',
  },
  vidu: {
    id: 'vidu',
    name: 'Vidu Q3 Pro',
    tier: 'Standard',
    type: 'video',
    creditsPerSecond: 5,
    minDuration: 4,
    maxDuration: 8,
    durationStep: 1,
    durationOptions: [4, 5, 6, 7, 8],
    defaultDuration: 4,
    resolutions: ['1280x720', '720x1280', '1024x1024'],
    supportsRefImage: true,
    maxRefImages: 1, // Vidu's start-end-to-video model does not lock the end frame — removed May 2026 to avoid misleading UX
    supportsAudio: true,
    description: 'Text, start frame, or start+end frame',
    color: 'blue',
  },
  kling: {
    id: 'kling',
    name: 'Kling 3.0',
    tier: 'Quality',
    type: 'video',
    creditsPerSecond: 8,
    minDuration: 3,
    maxDuration: 15,
    durationStep: 1,
    defaultDuration: 5,
    resolutions: ['1280x720', '720x1280'],
    supportsRefImage: true,
    supportsAudio: false,
    description: 'Cinematic, best bang for buck',
    color: 'purple',
  },
  sora: {
    id: 'sora',
    name: 'Sora 2',
    tier: 'Premium',
    type: 'video',
    creditsPerSecond: 12,
    minDuration: 4,
    maxDuration: 12,
    durationOptions: [4, 8, 12],
    defaultDuration: 4,
    resolutions: ['1280x720', '1024x1792', '1792x1024', '1024x1024'],
    supportsRefImage: false,
    supportsAudio: false,
    description: 'Highest quality, OpenAI',
    color: 'amber',
    retired: true, // Sora 2 is being shut down by OpenAI on Sept 24, 2026 — hidden from customer-facing UI
  },
  gpt_image: {
    id: 'gpt_image',
    name: 'GPT Image 1',
    tier: 'Image',
    type: 'image',
    creditsPerImage: 5,
    resolutions: ['1024x1024', '1536x1024', '1024x1536'],
    supportsRefImage: true,
    description: 'AI-generated images',
    color: 'pink',
  },
  // ============ 🔒 ADMIN-ONLY ENGINES ============
  // Filtered out for non-admin users in availableEngines (line ~470).
  seedance_spicy: {
    id: 'seedance_spicy',
    name: 'Seedance Spicy',
    tier: 'Admin',
    type: 'video',
    creditsPerSecond: 4,
    minDuration: 4,
    maxDuration: 12,
    durationStep: 1,
    defaultDuration: 5,
    resolutions: ['1280x720', '720x1280', '1920x1080', '1080x1920'],
    supportsRefImage: true,
    maxRefImages: 2,
    supportsAudio: true,
    description: 'Uncensored i2v for false-flag slideshow work',
    color: 'rose',
    adminOnly: true,
    etaHint: 'Slow tier — typically 5–10 min for a 5s clip. Up to 20 min for 12s.',
  },
  wan_spicy: {
    id: 'wan_spicy',
    name: 'Wan 2.2 Spicy',
    tier: 'Admin',
    type: 'video',
    creditsPerSecond: 4,
    minDuration: 3,
    maxDuration: 8,
    durationStep: 1,
    defaultDuration: 5,
    resolutions: ['1280x720', '720x1280', '1920x1080', '1080x1920'],
    supportsRefImage: true,
    maxRefImages: 1,
    supportsAudio: false,
    description: 'Uncensored i2v (Wan 2.2 Spicy by Alibaba)',
    color: 'rose',
    adminOnly: true,
    etaHint: 'Slow tier — typically 4–8 min for a 5s clip. Up to 15 min for 8s.',
  },
  seedream_sequential: {
    id: 'seedream_sequential',
    name: 'Character Sheets',
    tier: 'Admin',
    type: 'image',
    creditsPerImage: 6, // flat per-call (covers ~4 outputs)
    flatCredits: true,
    resolutions: ['1024x1024'],
    supportsRefImage: true,
    maxRefImages: 1,
    description: 'Multi-pose character sheet (Seedream Sequential)',
    color: 'rose',
    adminOnly: true,
    outputsMultiple: true,
  },
};

const tierColors = {
  emerald: 'border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10',
  blue: 'border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10',
  purple: 'border-purple-500/30 bg-purple-500/5 hover:bg-purple-500/10',
  amber: 'border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10',
  pink: 'border-pink-500/30 bg-pink-500/5 hover:bg-pink-500/10',
  rose: 'border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15', // 🔒 Admin
};

const tierBadgeColors = {
  emerald: 'bg-emerald-500/20 text-emerald-300',
  blue: 'bg-blue-500/20 text-blue-300',
  purple: 'bg-purple-500/20 text-purple-300',
  amber: 'bg-amber-500/20 text-amber-300',
  pink: 'bg-pink-500/20 text-pink-300',
  rose: 'bg-rose-500/25 text-rose-200',
};

const tierSelectedColors = {
  emerald: 'border-emerald-400 bg-emerald-500/15 ring-1 ring-emerald-400/50',
  blue: 'border-blue-400 bg-blue-500/15 ring-1 ring-blue-400/50',
  purple: 'border-purple-400 bg-purple-500/15 ring-1 ring-purple-400/50',
  amber: 'border-amber-400 bg-amber-500/15 ring-1 ring-amber-400/50',
  pink: 'border-pink-400 bg-pink-500/15 ring-1 ring-pink-400/50',
  rose: 'border-rose-400 bg-rose-500/20 ring-1 ring-rose-400/60',
};

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${m}:00`;
}

export const AIGenerateModal = ({ 
  isOpen, 
  onClose, 
  onAddToSlideshow,
  mode = 'both', // 'video', 'image', or 'both'
}) => {
  const { user } = useAuth();
  const isAdmin = (user?.role === 'admin' || user?.role === 'super_admin');

  // Admin "View as user" preview — when ON, hide admin-only engines so the
  // admin sees exactly what a regular user sees. Driven by localStorage,
  // controlled by the toggle in /admin. Reactive to changes from this tab
  // (custom event) and other tabs (storage event).
  const [viewAsUser, setViewAsUser] = useState(() => {
    try { return localStorage.getItem('wim_admin_view_as_user') === '1'; } catch { return false; }
  });
  useEffect(() => {
    const refresh = () => {
      try { setViewAsUser(localStorage.getItem('wim_admin_view_as_user') === '1'); } catch { /* ignore */ }
    };
    window.addEventListener('storage', refresh);
    window.addEventListener('wim_admin_view_as_user_changed', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('wim_admin_view_as_user_changed', refresh);
    };
  }, []);
  const effectiveAdmin = isAdmin && !viewAsUser;

  // Default engine: 'seedance' for everyone (was 'grok' but Grok is now admin-only).
  // Admins toggling "View as user" already get auto-fallback via the effect above
  // when an admin engine is selected, so this default works for both roles.
  const [selectedEngine, setSelectedEngine] = useState('seedance');

  // If "view as user" is toggled ON while an admin-only engine is selected,
  // fall back to the user-default ('seedance') so the modal stays in a sane state.
  useEffect(() => {
    const current = ENGINES[selectedEngine];
    if (current?.adminOnly && !effectiveAdmin) {
      setSelectedEngine('seedance');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAdmin]);
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(5);
  const [resolution, setResolution] = useState('1280x720');
  const [refImages, setRefImages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const fileInputRef = useRef(null);
  const cancelRef = useRef(false);
  const activeJobIdRef = useRef(null);

  // Grok-specific "mode" selector: 'text' | 'startframe' | 'references' | 'combined'
  // Tells the backend which xAI API shape to use. Non-Grok engines ignore this.
  const [grokMode, setGrokMode] = useState('text');

  // Library tab toggle: 'generate' (build a new clip) | 'recent' (browse past clips)
  const [activeTab, setActiveTab] = useState('generate');

  // Bonus: auto-download the finished file to the user's computer when generation
  // completes (in addition to it being saved server-side). Persists across renders
  // via localStorage so user preference sticks.
  const [autoDownload, setAutoDownload] = useState(() => {
    try { return localStorage.getItem('wim_ai_auto_download') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('wim_ai_auto_download', autoDownload ? '1' : '0'); } catch { /* ignore */ }
  }, [autoDownload]);

  // Live AI engine pricing fetched from backend (source of truth).
  // Falls back to ENGINES hard-coded defaults until loaded.
  const [livePricing, setLivePricing] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    fetch(`${API}/api/ai-engine-costs`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) setLivePricing(data); })
      .catch(() => { /* silent — fall back to defaults */ });
    return () => { cancelled = true; };
  }, [isOpen]);

  // Pricing getters — prefer live backend values, fall back to hard-coded defaults
  const pricePerSecond = (engineId) => {
    if (!livePricing) return ENGINES[engineId]?.creditsPerSecond ?? 0;
    const map = {
      seedance: livePricing.seedance_per_second,
      wan: livePricing.wan_per_second,
      grok: livePricing.grok_per_second,
      kling: livePricing.kling_per_second,
      sora: livePricing.sora_per_second,
    };
    return map[engineId] ?? ENGINES[engineId]?.creditsPerSecond ?? 0;
  };
  const priceForImage = () => livePricing?.gpt_image ?? ENGINES.gpt_image.creditsPerImage;

  const engine = ENGINES[selectedEngine];
  const isVideo = engine.type === 'video';
  const isGrok = selectedEngine === 'grok';
  const isVidu = selectedEngine === 'vidu';
  const isKling = selectedEngine === 'kling';
  const isSeedance = selectedEngine === 'seedance';
  // Atlas engines that support TRUE start+end frame continuity (end frame locks to user image).
  // Vidu was removed May 2026 — its start-end-to-video interpolates instead of locking.
  const isStartEndEngine = isKling || isSeedance;

  // Derive max images allowed based on Grok mode (and engine's cap)
  const maxImagesForMode = (() => {
    if (!isGrok) return engine.maxRefImages ?? 2;
    if (grokMode === 'text') return 0;
    if (grokMode === 'startframe') return 1;
    if (grokMode === 'references') return engine.maxRefImages ?? 7;
    if (grokMode === 'combined') return engine.maxRefImages ?? 7; // 1 start + up to 6 refs
    return 0;
  })();
  
  // Calculate credits — prefer live backend pricing
  const clipCredits = isVideo ? pricePerSecond(selectedEngine) * duration : priceForImage();
  const credits30s = isVideo ? pricePerSecond(selectedEngine) * 30 : null;
  const credits60s = isVideo ? pricePerSecond(selectedEngine) * 60 : null;

  const handleEngineChange = (engineId) => {
    const eng = ENGINES[engineId];
    setSelectedEngine(engineId);
    setDuration(eng.defaultDuration || 5);
    setResolution(eng.resolutions?.[0] || '1280x720');
    // Reset Grok mode to the safest default
    setGrokMode('text');
    // Clear any existing reference images
    refImages.forEach(img => URL.revokeObjectURL(img.previewUrl));
    setRefImages([]);
    setResult(null);
    setError('');
  };

  // When user switches Grok mode, clamp images to that mode's limit
  const handleGrokModeChange = (mode) => {
    setGrokMode(mode);
    const limits = { text: 0, startframe: 1, references: 7, combined: 7 };
    const limit = limits[mode] ?? 0;
    if (refImages.length > limit) {
      refImages.slice(limit).forEach(img => URL.revokeObjectURL(img.previewUrl));
      setRefImages(prev => prev.slice(0, limit));
    }
  };

  const handleRefImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const maxImages = maxImagesForMode;
    if (maxImages === 0) {
      toast.error('Select "Start Frame" or "References" mode to upload images');
      return;
    }
    if (refImages.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} image${maxImages === 1 ? '' : 's'} in this mode`);
      return;
    }
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const url = URL.createObjectURL(file);
      setRefImages(prev => [...prev, { file, previewUrl: url }]);
    }
    // Clamp duration if this engine caps reference-to-video length
    const cap = engine.refImageDurationCap;
    if (cap && duration > cap) {
      setDuration(cap);
    }
    // Reset the input so selecting the same file again re-triggers onChange
    if (e.target) e.target.value = '';
  };

  const removeRefImage = (index) => {
    setRefImages(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].previewUrl);
      updated.splice(index, 1);
      return updated;
    });
  };

  // XHR fallback for environments where a browser extension or service worker
  // has monkey-patched `fetch` and consumed the response body before we got it.
  // XHR has a completely separate code path extensions rarely touch.
  const xhrRequest = (url, { method = 'GET', headers = {}, body = null } = {}) => {
    return new Promise((resolve, reject) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url, true);
        xhr.withCredentials = true;
        Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.onload = () => resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, body: xhr.responseText || '' });
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));
        xhr.send(body);
      } catch (e) {
        reject(e);
      }
    });
  };

  const safeParseJSON = (raw) => {
    try {
      return raw ? JSON.parse(raw) : {};
    } catch {
      return { __rawBody: (raw || '').slice(0, 300) };
    }
  };

  // Do a fetch POST with automatic XHR fallback when a browser extension has
  // already consumed the response body (e.g., error "body stream already read").
  const requestJSON = async (url, { method = 'GET', headers = {}, body = null } = {}) => {
    try {
      const response = await fetch(url, { method, credentials: 'include', headers, body });
      const raw = await response.text();
      const data = safeParseJSON(raw);
      return { status: response.status, ok: response.ok, data };
    } catch (err) {
      const msg = (err && err.message) || '';
      if (msg.includes('body stream already read') || msg.includes("Failed to execute 'text'") || msg.includes("Failed to execute 'json'")) {
        // Fallback via XHR
        const xhr = await xhrRequest(url, { method, headers, body });
        return { status: xhr.status, ok: xhr.ok, data: safeParseJSON(xhr.body) };
      }
      throw err;
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description');
      return;
    }
    setGenerating(true);
    setError('');
    setResult(null);
    setElapsed(0);
    cancelRef.current = false;
    activeJobIdRef.current = null;
    const startTime = Date.now();
    const timerId = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    try {
      const formData = new FormData();
      formData.append('engine', selectedEngine);
      formData.append('prompt', prompt);
      formData.append('resolution', resolution);
      if (isGrok) {
        formData.append('grok_mode', grokMode);
      }
      if (isVideo) {
        formData.append('duration', duration);
      }
      refImages.forEach((img, i) => {
        formData.append(`ref_image_${i}`, img.file);
      });

      const token = getStoredToken();
      const { status, ok, data } = await requestJSON(`${API}/api/ai-generate`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });

      if (!ok) {
        throw new Error(data.detail || data.__rawBody || `Generation failed (HTTP ${status})`);
      }

      if (data.status === 'processing' && data.job_id) {
        activeJobIdRef.current = data.job_id;
        // Poll for completion
        const pollResult = await pollForResult(data.job_id, token);
        if (pollResult) {
          setResult(pollResult);
          handlePostGenerationSuccess(pollResult);
        }
      } else if (data.url) {
        const newResult = { url: data.url, type: engine.type };
        setResult(newResult);
        handlePostGenerationSuccess(newResult);
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (err) {
      if (cancelRef.current) {
        setError('Generation cancelled');
      } else {
        setError(err.message);
        toast.error(err.message);
      }
    } finally {
      clearInterval(timerId);
      setGenerating(false);
      setCancelling(false);
      activeJobIdRef.current = null;
    }
  };

  const handleCancel = async () => {
    const jobId = activeJobIdRef.current;
    cancelRef.current = true;
    setCancelling(true);
    if (!jobId) {
      // No job created yet — nothing to cancel server-side, just flip state
      setGenerating(false);
      setCancelling(false);
      setError('Generation cancelled');
      return;
    }
    try {
      const token = getStoredToken();
      await requestJSON(`${API}/api/ai-generate/${jobId}/cancel`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      toast.success('Generation cancelled');
    } catch {
      toast.error('Cancel request failed — stopping polling anyway');
    }
  };

  const pollForResult = async (jobId, token) => {
    const maxAttempts = 300; // 15 minutes at 3s intervals
    for (let i = 0; i < maxAttempts; i++) {
      if (cancelRef.current) return null;
      await new Promise(r => setTimeout(r, 3000));
      if (cancelRef.current) return null;
      try {
        const { data } = await requestJSON(`${API}/api/ai-generate/${jobId}/status`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (data.status === 'completed' && data.url) {
          return { url: data.url, type: engine.type };
        }
        if (data.status === 'failed') {
          if (data.cancelled) return null;
          throw new Error(data.error || 'Generation failed');
        }
      } catch (err) {
        if (err.message && err.message.includes('failed')) throw err;
      }
    }
    throw new Error('Generation timed out');
  };

  const handleDownload = async () => {
    if (!result?.url) return;
    try {
      const response = await fetch(result.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `ai_${engine.type}_${Date.now()}.${isVideo ? 'mp4' : 'png'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(result.url, '_blank');
    }
  };

  // Fired the moment a generation finishes successfully. Handles:
  //   1) Toast confirming the clip was saved to the user's AI Clips Library
  //   2) Optional auto-download to the user's computer (opt-in checkbox)
  const handlePostGenerationSuccess = async (newResult) => {
    if (!newResult?.url) return;
    toast.success('Saved to your AI Clips Library', {
      description: 'You can re-use or download it anytime.',
      action: {
        label: 'View library',
        onClick: () => {
          try { window.open('/my-ai-clips', '_blank'); } catch { /* ignore */ }
        },
      },
    });
    if (autoDownload) {
      try {
        const isImg = !isVideo;
        const r = await fetch(newResult.url);
        const blob = await r.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `ai_${engine.type}_${Date.now()}.${isImg ? 'png' : 'mp4'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch {
        // Silent — user can still hit the manual download button.
      }
    }
  };

  const handleAddToSlideshow = () => {
    if (!result?.url || !onAddToSlideshow) return;
    onAddToSlideshow({
      url: result.url,
      type: isVideo ? 'video' : 'photo',
      duration: isVideo ? duration : undefined,
      engine: engine.name,
    });
    toast.success(`${isVideo ? 'Video clip' : 'Image'} added to slideshow`);
  };

  if (!isOpen) return null;

  const availableEngines = Object.values(ENGINES).filter(e => {
    if (e.retired) return false; // Hide retired engines (e.g. Sora 2 — shutting down Sept 2026)
    if (e.adminOnly && !effectiveAdmin) return false; // 🔒 Admin Studio engines only visible to admins (and hidden when "view as user" is ON)
    if (mode === 'video') return e.type === 'video';
    if (mode === 'image') return e.type === 'image';
    return true;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-generate-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span>AI Generate</span>
              <CoachEye targetId="aigen.create" label="Create video clips or images with AI" />
            </h2>
            <p className="text-zinc-400 text-sm">Create video clips or images with AI</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="ai-generate-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs: Generate (build new) | Recent (re-use past clips) */}
        <div className="flex border-b border-zinc-800 px-6 gap-1">
          {[
            { id: 'generate', label: 'Generate', icon: Sparkles },
            { id: 'recent', label: 'Recent', icon: History },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === id
                  ? 'border-emerald-400 text-white'
                  : 'border-transparent text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid={`ai-modal-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Recent tab body */}
        {activeTab === 'recent' && (
          <div className="p-6">
            <p className="text-xs text-zinc-500 mb-4">
              Every clip you&apos;ve generated is saved here automatically. Pick one to drop straight into your slideshow, or click the <FileText className="inline w-3 h-3 -mt-0.5" /> icon to re-use a clip&apos;s prompt as the starting point for a new render.
            </p>
            <AIClipsLibrary
              compact
              onUseClip={onAddToSlideshow ? (clip) => {
                onAddToSlideshow(clip);
                toast.success(`${clip.type === 'video' ? 'Video clip' : 'Image'} added to slideshow`);
              } : undefined}
              onReusePrompt={(clip) => {
                setPrompt(clip.prompt || '');
                // Also carry over engine + resolution + duration if the source
                // engine is still available to this user — small quality-of-
                // life bonus that saves another 2 clicks.
                if (clip.engine && availableEngines.some((e) => e.id === clip.engine)) {
                  handleEngineChange(clip.engine);
                  if (clip.resolution) setResolution(clip.resolution);
                  if (clip.duration) setDuration(clip.duration);
                }
                setActiveTab('generate');
              }}
            />
          </div>
        )}

        {activeTab === 'generate' && (
        <div className="p-6 space-y-6">
          {/* Engine Selection */}
          <div>
            <Label className="text-zinc-300 mb-3 block">Select Engine</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {availableEngines.map((eng) => (
                <button
                  key={eng.id}
                  onClick={() => handleEngineChange(eng.id)}
                  data-testid={`engine-${eng.id}`}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    selectedEngine === eng.id
                      ? tierSelectedColors[eng.color]
                      : tierColors[eng.color]
                  }`}
                >
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${tierBadgeColors[eng.color]}`}>
                    {eng.adminOnly ? '🔒 Admin' : eng.tier}
                  </span>
                  <p className="text-white text-sm font-medium mt-1.5">{eng.name}</p>
                  <p className="text-zinc-500 text-[10px] mt-0.5">{eng.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Prompt */}
          <div>
            <Label className="text-zinc-300 mb-2 block">
              Describe what you want to {isVideo ? 'see in the video' : 'generate'}
            </Label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={isVideo 
                ? "e.g., A cinematic aerial shot of ocean waves crashing on a rocky coastline at sunset..." 
                : "e.g., A dreamy watercolor painting of a moonlit lake surrounded by mountains..."
              }
              className="input-dark w-full min-h-[80px] resize-y py-3 px-4 rounded-xl"
              data-testid="ai-prompt-input"
              rows={3}
            />
          </div>

          {/* Duration (video only) */}
          {isVideo && (
            <div>
              <Label className="text-zinc-300 mb-2 block">
                Clip Length: {formatDuration(duration)}
              </Label>
              {engine.durationOptions ? (
                <div className="flex gap-2 flex-wrap items-center">
                  {engine.durationOptions.map(d => {
                    const cap = engine.refImageDurationCap;
                    const locked = cap && refImages.length > 0 && d > cap;
                    return (
                      <button
                        key={d}
                        onClick={() => !locked && setDuration(d)}
                        disabled={locked}
                        title={locked ? `Reference-to-video is capped at ${cap}s by xAI` : undefined}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                          locked
                            ? 'border-zinc-800 bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                            : duration === d
                              ? 'border-white/40 bg-white/10 text-white'
                              : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
                        }`}
                        data-testid={`duration-preset-${d}`}
                      >
                        {d}s
                      </button>
                    );
                  })}
                  {/* Custom seconds input */}
                  {(() => {
                    const cap = engine.refImageDurationCap;
                    const effectiveMax = (cap && refImages.length > 0) ? Math.min(engine.maxDuration, cap) : engine.maxDuration;
                    const isCustom = !engine.durationOptions.includes(duration);
                    return (
                      <div
                        className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 transition-all ${
                          isCustom
                            ? 'border-white/40 bg-white/10'
                            : 'border-zinc-700 bg-zinc-800/50'
                        }`}
                        title={`Any value ${engine.minDuration}–${effectiveMax}s`}
                      >
                        <input
                          type="number"
                          min={engine.minDuration}
                          max={effectiveMax}
                          step={1}
                          value={duration}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!Number.isFinite(v)) return;
                            const clamped = Math.min(effectiveMax, Math.max(engine.minDuration, v));
                            setDuration(clamped);
                          }}
                          className={`w-12 bg-transparent text-sm font-medium outline-none text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                            isCustom ? 'text-white' : 'text-zinc-400'
                          }`}
                          data-testid="duration-custom-input"
                        />
                        <span className={`text-sm ${isCustom ? 'text-white' : 'text-zinc-500'}`}>s</span>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <Slider
                  value={[duration]}
                  onValueChange={([val]) => setDuration(val)}
                  min={engine.minDuration}
                  max={engine.maxDuration}
                  step={engine.durationStep}
                />
              )}
              {engine.refImageDurationCap && refImages.length > 0 && (
                <p className="text-amber-400/80 text-xs mt-2">
                  Reference-to-video is capped at {engine.refImageDurationCap}s by xAI.
                </p>
              )}
              <div className="flex justify-between text-zinc-600 text-xs mt-1">
                <span>{engine.minDuration}s min</span>
                <span>{engine.maxDuration}s max</span>
              </div>
            </div>
          )}

          {/* Resolution */}
          <div>
            <Label className="text-zinc-300 mb-2 block">Aspect Ratio</Label>
            <div className="flex gap-2 flex-wrap">
              {engine.resolutions?.map(res => {
                const [w, h] = res.split('x').map(Number);
                const label = w > h ? 'Landscape' : w < h ? 'Portrait' : 'Square';
                return (
                  <button
                    key={res}
                    onClick={() => setResolution(res)}
                    className={`px-4 py-2 rounded-lg border text-sm transition-all ${
                      resolution === res
                        ? 'border-white/40 bg-white/10 text-white'
                        : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
                    }`}
                  >
                    {label} ({res})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grok Mode Selector (only for Grok engine) */}
          {isGrok && (
            <div>
              <Label className="text-zinc-300 mb-2 block">Mode</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mb-3 bg-zinc-900/50 border border-white/10 rounded-xl p-1.5">
                {[
                  { id: 'text', label: 'Text Only' },
                  { id: 'startframe', label: 'Start Frame' },
                  { id: 'references', label: 'References' },
                  { id: 'combined', label: 'Start + Refs' },
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => handleGrokModeChange(m.id)}
                    className={`py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                      grokMode === m.id
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md'
                        : 'text-zinc-400 hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`grok-mode-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {/* Mode-specific helper copy */}
              <div className="text-xs text-zinc-400 mb-3 leading-relaxed">
                {grokMode === 'text' && (
                  <span>Generate purely from your text description — no images needed.</span>
                )}
                {grokMode === 'startframe' && (
                  <span className="text-emerald-300/90">
                    🎬 <strong>Your image becomes the EXACT first frame</strong> of the video. Best for animating a specific photo (your pet, your cover art, a product shot). Upload 1 image.
                  </span>
                )}
                {grokMode === 'references' && (
                  <span className="text-amber-300/90">
                    🎨 <strong>Images are visual guides only</strong> — none appear as-is. The video is generated fresh using your references for characters, objects, or locations. Upload 1–7 images and tag them in your prompt as <code className="bg-black/40 px-1 rounded">@Image1</code>, <code className="bg-black/40 px-1 rounded">@Image2</code>, etc.
                  </span>
                )}
                {grokMode === 'combined' && (
                  <span className="text-sky-300/90">
                    ✨ <strong>First image = start frame; images 2–7 = references</strong>. Video starts from @Image1 then uses remaining images to guide characters/objects/places. Tag them in your prompt as <code className="bg-black/40 px-1 rounded">@Image2</code> onward.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Reference Images */}
          {engine.supportsRefImage && maxImagesForMode > 0 && (
            <div>
              <Label className="text-zinc-300 mb-2 block">
                {isGrok
                  ? (grokMode === 'startframe'
                      ? 'Start Frame Image'
                      : grokMode === 'combined'
                        ? `Images (${refImages.length} of ${maxImagesForMode}) — first is start frame, rest are references`
                        : `Reference Images (${refImages.length} of ${maxImagesForMode})`)
                  : (isStartEndEngine)
                    ? `Images (${refImages.length} of 2) — 1 = Start Frame, 2 = Start + End Frame`
                    : `Reference Images (optional, max ${engine.maxRefImages ?? 2})`}
              </Label>
              {isStartEndEngine && refImages.length === 0 && (
                <p className="text-xs text-zinc-400 mb-2 leading-relaxed">
                  💡 Upload <strong>1 image</strong> to make it the exact first frame, or
                  <strong> 2 images</strong> to define both start AND end frames (great for dance, action,
                  or scene-to-scene continuity). Or generate from text alone.
                </p>
              )}
              <div className="flex gap-3 items-start flex-wrap">
                {refImages.map((img, i) => {
                  // Label overlays vary by mode
                  let badge = null;
                  if (isGrok) {
                    if (grokMode === 'startframe') badge = 'START';
                    else if (grokMode === 'combined') badge = i === 0 ? 'START' : `@Image${i+1}`;
                    else if (grokMode === 'references') badge = `@Image${i+1}`;
                  } else if (isStartEndEngine) {
                    badge = i === 0 ? 'START' : 'END';
                  }
                  return (
                    <div key={i} className="relative w-20 h-20 group">
                      <img src={img.previewUrl} alt={`Ref ${i+1}`} className="w-full h-full object-cover rounded-lg" />
                      {badge && (
                        <span className={`absolute bottom-1 left-1 text-white text-[10px] font-mono px-1.5 py-0.5 rounded ${
                          badge === 'START' ? 'bg-emerald-600/90' : badge === 'END' ? 'bg-purple-600/90' : 'bg-black/70'
                        }`}>
                          {badge}
                        </span>
                      )}
                      <button
                        onClick={() => removeRefImage(i)}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full p-0.5"
                        data-testid={`ref-image-remove-${i}`}
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  );
                })}
                {refImages.length < maxImagesForMode && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-zinc-700 hover:border-zinc-500 flex items-center justify-center transition-colors"
                    data-testid="ai-ref-image-upload-btn"
                  >
                    <Plus className="h-5 w-5 text-zinc-500" />
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleRefImageUpload}
                  className="hidden"
                />
              </div>
              {/* Best-practice tip (only for multi-ref modes) */}
              {isGrok && (grokMode === 'references' || grokMode === 'combined') && (
                <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                  💡 Tip: one image per person/thing works best. Always mention each <code>@ImageN</code> in your prompt — unmentioned references get weakly used or ignored.
                </p>
              )}
            </div>
          )}

          {/* Pricing Display */}
          <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-300 font-medium">This {isVideo ? 'clip' : 'image'}</span>
              <span className="text-white font-bold text-lg">{clipCredits} credits</span>
            </div>
            {isVideo && (
              <div className="flex gap-6 text-xs text-zinc-500">
                <div className="flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  <span>30s of video: {credits30s} credits</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>60s: {credits60s} credits</span>
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Result Preview */}
          {result && (
            <div className="border border-zinc-700 rounded-xl overflow-hidden">
              <div className="bg-black">
                {result.type === 'video' ? (
                  <video src={result.url} controls className="w-full max-h-80" />
                ) : (
                  <img src={result.url} alt="Generated" className="w-full max-h-80 object-contain" />
                )}
              </div>
              <div className="p-4 flex gap-3">
                <Button onClick={handleDownload} className="bg-green-600 hover:bg-green-500 text-white flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                {onAddToSlideshow && (
                  <Button onClick={handleAddToSlideshow} className="bg-purple-600 hover:bg-purple-500 text-white flex-1">
                    <Plus className="h-4 w-4 mr-2" />
                    Add to Slideshow
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Slow-tier ETA notice — shown for engines with an `etaHint` so
              users (admins, mostly) know not to assume the app is broken when
              renders take longer than the usual ~2 min fast-tier baseline. */}
          {!result && engine?.etaHint && (
            <div
              className="flex items-start gap-2 p-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] text-amber-100/90 text-xs"
              data-testid="engine-eta-hint"
            >
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-400" />
              <div>
                <p className="font-medium text-amber-200">This engine takes a while</p>
                <p className="mt-0.5 text-amber-100/70">{engine.etaHint}</p>
              </div>
            </div>
          )}

          {/* Generate Button */}
          {!result && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none w-fit" data-testid="ai-auto-download-label">
                <input
                  type="checkbox"
                  checked={autoDownload}
                  onChange={(e) => setAutoDownload(e.target.checked)}
                  className="w-4 h-4 rounded bg-zinc-800 border-zinc-700 text-emerald-500 focus:ring-emerald-500/50"
                  data-testid="ai-auto-download-checkbox"
                />
                <span>Auto-download to my computer when finished</span>
              </label>
              <div className="flex gap-3">
              <Button
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                className="flex-1 py-6 text-lg bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-medium rounded-xl"
                data-testid="ai-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating... {elapsed > 0 && `(${formatDuration(elapsed)})`}
                  </>
                ) : (
                  <>
                    {isVideo ? <Play className="h-5 w-5 mr-2" /> : <ImageIcon className="h-5 w-5 mr-2" />}
                    Generate {isVideo ? 'Video Clip' : 'Image'} ({clipCredits} credits)
                  </>
                )}
              </Button>
              {generating && (
                <Button
                  onClick={handleCancel}
                  disabled={cancelling}
                  variant="outline"
                  className="py-6 px-6 text-base border-red-500/50 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-xl"
                  data-testid="ai-generate-cancel-btn"
                >
                  {cancelling ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Cancelling…
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </>
                  )}
                </Button>
              )}
              </div>
            </div>
          )}

          {/* Generate Another after result */}
          {result && (
            <Button
              onClick={() => setResult(null)}
              variant="outline"
              className="w-full border-zinc-700 text-zinc-300"
            >
              Generate Another
            </Button>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default AIGenerateModal;
